import * as React from "react";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Calendar, CalendarPanel, CALENDAR_POPOVER_CLASS } from "../Calendar";
import type { CalendarProps } from "../Calendar";
import type { DateRange } from "react-day-picker";

import type { TriggerAccessibilityProps } from "../DatePicker";

export type DateRangeValue = {
  from: Date;
  to: Date;
};

export interface DateRangePickerBaseProps {
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  error?: boolean;
  value?: DateRangeValue | DateRange | null | undefined;
  onChange?: (range: DateRangeValue | undefined) => void;
  onSelect?: (range: DateRangeValue | undefined) => void;
  placeholder: string;
  /** The popup's header, e.g. "Pick a period:". */
  title: string;
  /** The popup's close button. */
  closeLabel: string;
  /** The popup's Save -- the picked range applies only then. */
  saveLabel: string;
  cancelLabel: string;
  clearLabel: string;
  nextMonthLabel: string;
  previousMonthLabel: string;
  dir: "ltr" | "rtl";
  locale: Locale;
  dateFormat?: string;
  numberOfMonths?: number;
  disabled?: boolean;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  calendarProps?: Partial<
    Omit<
      CalendarProps,
      | "mode"
      | "selected"
      | "onSelect"
      | "required"
      | "dir"
      | "locale"
      | "nextMonthLabel"
      | "previousMonthLabel"
    >
  >;
}

export type DateRangePickerProps = DateRangePickerBaseProps &
  TriggerAccessibilityProps;

export function DateRangePicker({
  id,
  ref,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  error,
  value,
  onChange,
  onSelect,
  placeholder,
  title,
  closeLabel,
  saveLabel,
  cancelLabel,
  clearLabel,
  nextMonthLabel,
  previousMonthLabel,
  dir,
  locale,
  dateFormat = "PP",
  numberOfMonths = 1,
  disabled = false,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  calendarProps,
  ...accessibilityProps
}: DateRangePickerProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  // A 2-up calendar doesn't fit a phone screen regardless of what the caller
  // asked for, so cap the rendered months to 1 below the `md` breakpoint.
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return true;
    }
    return window.matchMedia("(min-width: 768px)").matches;
  });

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 768px)");
    const handleChange = () => setIsDesktop(desktop.matches);
    handleChange();
    desktop.addEventListener("change", handleChange);
    return () => desktop.removeEventListener("change", handleChange);
  }, []);

  const effectiveNumberOfMonths = isDesktop ? numberOfMonths : 1;

  // The range picked in the open popup -- applied only on Save (Figma
  // 2818:116890); closing any other way drops it.
  const [internalRange, setInternalRange] = React.useState<
    DateRange | undefined
  >(() =>
    value?.from ? { from: value.from, to: value.to ?? undefined } : undefined,
  );

  // Every opening starts from the current value, however it was opened.
  React.useEffect(() => {
    if (!isOpen) return;
    setInternalRange(
      value?.from ? { from: value.from, to: value.to ?? undefined } : undefined,
    );
  }, [isOpen, value]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    controlledOnOpenChange?.(nextOpen);
  };

  const handleSave = () => {
    if (!internalRange?.from || !internalRange.to) return;
    const completeRange: DateRangeValue = {
      from: internalRange.from,
      to: internalRange.to,
    };
    onChange?.(completeRange);
    onSelect?.(completeRange);
    handleOpenChange(false);
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setInternalRange(undefined);
    onChange?.(undefined);
    onSelect?.(undefined);
  };

  const accessibleTriggerLabel =
    accessibilityProps.triggerLabel ??
    accessibilityProps.triggerAriaLabel ??
    accessibilityProps["aria-label"];

  const isInvalid = error || ariaInvalid === true || ariaInvalid === "true";
  const hasCompleteValue = Boolean(value?.from && value.to);
  const formattedRange =
    value?.from && value.to
      ? `${format(value.from, dateFormat, { locale })} - ${format(value.to, dateFormat, { locale })}`
      : null;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          id={id}
          aria-describedby={ariaDescribedby}
          aria-invalid={isInvalid ? true : undefined}
          type="button"
          variant="secondary"
          aria-label={accessibleTriggerLabel}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-start text-body-m-r gap-2",
            !hasCompleteValue && "text-text-placeholder",
            isInvalid &&
              "border-fail-text text-fail-text focus-visible:border-fail-text",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 text-secondary-icon" />
          <span className="truncate">{formattedRange ?? placeholder}</span>
          {hasCompleteValue && (
            <span
              role="button"
              tabIndex={0}
              aria-label={clearLabel}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleClear(e as unknown as React.MouseEvent);
                }
              }}
              className="ms-auto inline-flex items-center justify-center rounded-sm p-0.5 text-secondary-icon transition-colors hover:text-text focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <X className="size-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={CALENDAR_POPOVER_CLASS} align="start">
        <CalendarPanel
          title={title}
          closeLabel={closeLabel}
          saveLabel={saveLabel}
          cancelLabel={cancelLabel}
          onSave={handleSave}
          onCancel={() => handleOpenChange(false)}
          // Both ends picked -- a range is applied whole or not at all.
          saveDisabled={!internalRange?.from || !internalRange.to}
        >
          <Calendar
            dir={dir}
            locale={locale}
            nextMonthLabel={nextMonthLabel}
            previousMonthLabel={previousMonthLabel}
            numberOfMonths={effectiveNumberOfMonths}
            resetOnSelect={true}
            {...calendarProps}
            mode="range"
            selected={internalRange}
            onSelect={setInternalRange}
          />
        </CalendarPanel>
      </PopoverContent>
    </Popover>
  );
}
